<template>
    <div class="options" :class="{ 'has-selected': selectedScreenGids.length > 0 }">
        <div v-for="(gid, index) in selectedScreenGids" :key="gid" class="option">
            <div class="option__header">
                <h3 class="option__title">Экран №{{ index + 1 }}</h3>
                <div class="option__id-and-actions">
                    <label class="option__id" :title="'ID: ' + gid">
                        ID: {{ gid }} &nbsp; · &nbsp;
                        <img src="/images/size.svg" alt="size" />
                        {{ getScreenSize(gid).width }} x {{ getScreenSize(gid).height }}
                        &nbsp; · &nbsp;
                        <span class="oid-badge">OID: {{ getScreen(gid)?.oid }}</span>
                    </label>
                </div>
            </div>

            <div class="option__body">
                <!-- Адрес экрана -->
                <div class="screen-info">
                    <div class="screen-info__address">
                        {{ getScreen(gid)?.frame?.address || 'Адрес не указан' }}
                    </div>
                    <div class="screen-info__city">
                        {{ getScreen(gid)?.frame?.city || '' }}
                    </div>
                </div>

                <!-- Выбор креативов -->
                <div class="creatives-option">
                    <div class="creatives-option__header">
                        <h3 class="creatives-option__title">Креативы</h3>
                        <div class="creatives-option__actions">
                            <img v-if="!hasCreatives(gid)" alt="red_star" class="is-empty" src="/images/red_star.svg" />
                            <button v-if="getFilteredCreatives(gid).length > 0" class="btn-toggle" @click="toggleAllCreatives(gid)">
                                {{ isAllCreativesSelected(gid) ? 'Очистить' : 'Выбрать все' }}
                            </button>
                        </div>
                    </div>
                    <div class="creatives-option__grid">
                        <div 
                            v-for="creative in getCreativesForScreen(gid)" 
                            :key="creative.id"
                            class="creative-card"
                            :class="{ 'creative-card--disabled': creative.type !== type }"
                        >
                            <div class="creative-card__preview" @click="creative.type === type && toggleCreative(gid, creative.id)">
                                <img v-if="creative.type === 'picture' && creative.url" :src="creative.url" :alt="creative.name">
                                <video v-else-if="creative.type === 'video' && creative.url" :src="creative.url" muted loop></video>
                                <div v-else class="creative-card__placeholder">
                                    {{ creative.type === 'video' ? '🎬' : '🖼' }}
                                </div>
                                <input 
                                    type="checkbox"
                                    class="creative-card__checkbox"
                                    :checked="isCreativeSelected(gid, creative.id)"
                                    :disabled="creative.type !== type"
                                    @click.stop="creative.type === type && toggleCreative(gid, creative.id)"
                                >
                            </div>
                            <div class="creative-card__info">
                                <div class="creative-card__name" :title="creative.name">{{ creative.name }}</div>
                                <div class="creative-card__size">
                                    ID: {{ creative.id }} &nbsp; · &nbsp;
                                    <img src="/images/size.svg" alt="size" />
                                    {{ creative.width }} × {{ creative.height }}
                                </div>
                            </div>
                        </div>
                        <div v-if="getCreativesForScreen(gid).length === 0" class="no-creatives">
                            Нет креативов для OID: {{ getScreen(gid)?.oid }}
                        </div>
                        <div v-else-if="getFilteredCreatives(gid).length === 0" class="no-creatives">
                            Нет креативов типа "{{ type === 'video' ? 'Видео' : 'Картинка' }}" для этого экрана
                        </div>
                    </div>
                </div>

                <!-- Временные настройки -->
                <WeekdayTimeSelector 
                    :modelValue="getTimeSettings(gid)"
                    @update:modelValue="updateTimeSettings(gid, $event)"
                    :allowed-time-intervals="getAllowedTimeIntervals(gid)"
                    :active-periods="getActivePeriods(gid)"
                />
            </div>
        </div>
        
        <div v-if="selectedScreenGids.length === 0" class="no-selection">
            <p>Выберите экраны на карте для настройки объявлений</p>
        </div>
    </div>
</template>

<script>
import WeekdayTimeSelector from './WeekdayTimeSelector.vue';

export default {
    name: 'AdvertisementOptions',
    components: {
        WeekdayTimeSelector,
    },
    props: {
        screens: {
            type: Array,
            required: true,
        },
        selectedScreenGids: {
            type: Array,
            default: () => [],
        },
        creativesByOid: {
            type: Object,
            default: () => ({}),
        },
        creativesWithoutOid: {
            type: Array,
            default: () => [],
        },
        type: {
            type: String,
            default: 'picture',
        },
        advertisementPeriod: {
            type: Object,
            default: () => ({ start: '', end: '' }),
        },
        screenSettings: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['update:screenSettings'],
    methods: {
        getScreen(gid) {
            return this.screens.find(s => s.gid === gid);
        },
        
        getScreenSize(gid) {
            const screen = this.getScreen(gid);
            return {
                width: screen?.frame?.width || '?',
                height: screen?.frame?.height || '?',
            };
        },
        
        getCreativesForScreen(gid) {
            const screen = this.getScreen(gid);
            if (!screen) return [];
            
            const oid = screen.oid;
            const oidCreatives = this.creativesByOid[oid] || [];
            return [...oidCreatives, ...this.creativesWithoutOid];
        },
        
        getFilteredCreatives(gid) {
            return this.getCreativesForScreen(gid).filter(c => c.type === this.type);
        },
        
        hasCreatives(gid) {
            const settings = this.screenSettings[gid];
            return settings && settings.creativeIds && settings.creativeIds.length > 0;
        },
        
        isCreativeSelected(gid, creativeId) {
            const settings = this.screenSettings[gid];
            return settings && settings.creativeIds && settings.creativeIds.includes(creativeId);
        },
        
        toggleCreative(gid, creativeId) {
            const settings = { ...this.screenSettings };
            if (!settings[gid]) {
                settings[gid] = { creativeIds: [], timeSettings: [], period: { ...this.advertisementPeriod } };
            }
            
            const creativeIds = [...(settings[gid].creativeIds || [])];
            const index = creativeIds.indexOf(creativeId);
            
            if (index > -1) {
                creativeIds.splice(index, 1);
            } else {
                creativeIds.push(creativeId);
            }
            
            settings[gid] = { ...settings[gid], creativeIds };
            this.$emit('update:screenSettings', settings);
        },
        
        isAllCreativesSelected(gid) {
            const filtered = this.getFilteredCreatives(gid);
            if (filtered.length === 0) return false;
            const settings = this.screenSettings[gid];
            if (!settings || !settings.creativeIds) return false;
            return filtered.every(c => settings.creativeIds.includes(c.id));
        },
        
        toggleAllCreatives(gid) {
            const settings = { ...this.screenSettings };
            if (!settings[gid]) {
                settings[gid] = { creativeIds: [], timeSettings: [], period: { ...this.advertisementPeriod } };
            }
            
            const filtered = this.getFilteredCreatives(gid);
            if (this.isAllCreativesSelected(gid)) {
                // Очистить все (убираем только отфильтрованные)
                const filteredIds = filtered.map(c => c.id);
                settings[gid] = { 
                    ...settings[gid], 
                    creativeIds: (settings[gid].creativeIds || []).filter(id => !filteredIds.includes(id))
                };
            } else {
                // Выбрать все отфильтрованные
                const existingIds = settings[gid].creativeIds || [];
                const newIds = filtered.map(c => c.id).filter(id => !existingIds.includes(id));
                settings[gid] = { 
                    ...settings[gid], 
                    creativeIds: [...existingIds, ...newIds]
                };
            }
            
            this.$emit('update:screenSettings', settings);
        },
        
        getTimeSettings(gid) {
            const settings = this.screenSettings[gid];
            return settings?.timeSettings || [];
        },
        
        updateTimeSettings(gid, newTimeSettings) {
            const settings = { ...this.screenSettings };
            if (!settings[gid]) {
                settings[gid] = { creativeIds: [], timeSettings: [], period: { ...this.advertisementPeriod } };
            }
            settings[gid] = { ...settings[gid], timeSettings: newTimeSettings };
            this.$emit('update:screenSettings', settings);
        },
        
        /**
         * Возвращает разрешенные временные интервалы для экрана (из timeSettings подкампании)
         */
        getAllowedTimeIntervals(gid) {
            const screen = this.getScreen(gid);
            if (!screen || !screen.timeSettings) return {};
            
            // screen.timeSettings уже в нужном формате { [day]: [{startTime, endTime}] }
            // Если это массив, конвертируем в объект по дням
            if (Array.isArray(screen.timeSettings)) {
                const result = {};
                screen.timeSettings.forEach(ts => {
                    if (!result[ts.day]) {
                        result[ts.day] = [];
                    }
                    result[ts.day].push({
                        day: ts.day,
                        startTime: ts.startTime,
                        endTime: ts.endTime,
                    });
                });
                return result;
            }
            
            return screen.timeSettings;
        },
        
        /**
         * Возвращает активные периоды для экрана
         */
        getActivePeriods(gid) {
            const screen = this.getScreen(gid);
            if (!screen) return [];
            
            // Используем periods из подкампании или период подкампании
            if (screen.periods && screen.periods.length > 0) {
                return screen.periods;
            }
            
            if (screen.period_start && screen.period_end) {
                return [{
                    start: screen.period_start.split(' ')[0],
                    end: screen.period_end.split(' ')[0],
                }];
            }
            
            return [];
        },
    },
};
</script>

<style scoped>
.options {
    background: transparent;
}

.option {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.option__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
}

.option__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.option__id {
    font-size: 13px;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 4px;
}

.option__id img {
    width: 14px;
    height: 14px;
    opacity: 0.6;
}

.oid-badge {
    background: #e0e7ff;
    color: #4338ca;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
}

.screen-info {
    margin-bottom: 16px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
}

.screen-info__address {
    font-weight: 500;
    color: #374151;
}

.screen-info__city {
    font-size: 13px;
    color: #6b7280;
    margin-top: 4px;
}

/* Creatives option - стили как в CreativesOption.vue */
.creatives-option {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
}

.creatives-option__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

.creatives-option__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e40af;
}

.creatives-option__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.creatives-option__actions .is-empty {
    /* uses global .is-empty */
}

.btn-toggle {
    min-width: 110px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: linear-gradient(90deg, rgba(0, 166, 255, 0.08), rgba(0, 120, 255, 0.06));
    color: #1e40af;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
}

.btn-toggle:hover {
    background: rgba(37, 99, 235, 0.12);
}

.btn-toggle:active {
    transform: scale(0.97);
}

.creatives-option__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px;
    width: 100%;
}

.creative-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1 1 calc(33.333% - 16px);
    max-width: calc(33.333% - 16px);
    box-sizing: border-box;
    transition: all 0.2s;
}

.creative-card:hover:not(.creative-card--disabled) {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.creative-card--disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.creative-card__preview {
    position: relative;
    width: 100%;
    height: 180px;
    background: #f9fafb;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.creative-card__preview img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.creative-card__preview video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    background: #000;
}

.creative-card__placeholder {
    font-size: 48px;
    color: #94a3b8;
}

.creative-card__checkbox {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 18px;
    height: 18px;
    accent-color: #2563eb;
    cursor: pointer;
}

.creative-card__info {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.creative-card__name {
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.creative-card__size {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #64748b;
}

.creative-card__size img {
    width: 15px;
    margin-left: 4px;
}

.no-creatives {
    flex: 1 1 100%;
    padding: 20px;
    text-align: center;
    color: #6b7280;
    background: #f9fafb;
    border-radius: 8px;
}

/* Responsive styles */
@media (max-width: 1024px) {
    .creative-card {
        flex: 1 1 calc(50% - 16px);
        max-width: calc(50% - 16px);
    }
}

@media (max-width: 768px) {
    .creatives-option__grid {
        gap: 12px;
        padding: 12px;
    }
    
    .creative-card {
        flex: 1 1 calc(50% - 12px);
        max-width: calc(50% - 12px);
    }
    
    .creative-card__preview {
        height: 160px;
    }
}

@media (max-width: 480px) {
    .creatives-option__grid {
        gap: 10px;
    }
    
    .creative-card {
        flex: 1 1 100%;
        max-width: 100%;
    }
    
    .creative-card__preview {
        height: 140px;
    }
}

.no-selection {
    padding: 40px 20px;
    text-align: center;
    color: #6b7280;
    background: #f9fafb;
    border-radius: 12px;
}

.no-selection p {
    margin: 0;
}
</style>
